import type { AxiosError } from 'axios';

/**
 * Human-readable message from a failed API request (Flask often returns
 * `{ message, error, code }` on 4xx).
 */
export function getApiErrorMessage(error: unknown, fallback = 'Request failed'): string {
  const ax = error as AxiosError<{
    message?: string;
    error?: string;
    detail?: string;
  }>;
  const data = ax.response?.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const msg = data.message ?? data.error ?? data.detail;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
  }
  return fallback;
}

/**
 * API response unwrapping utilities.
 *
 * The Flask backend returns responses in 3 patterns:
 *   1. Direct array:   [item, item, ...]
 *   2. Wrapper object:  { logs: [...], pagination: {...} }
 *   3. Direct object:   { field: value, ... }
 *
 * These helpers normalize the inconsistency at the API layer so components
 * always receive the shape they expect.
 */

/** Safely extract an array from an API response that might be wrapped. */
export function unwrapArray<T = any>(data: unknown, key?: string): T[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    // Try the explicit key first
    if (key && Array.isArray(obj[key])) return obj[key] as T[];
    // Try common wrapper keys
    for (const k of ['items', 'data', 'results', 'entries', 'logs', 'documents', 'jobs']) {
      if (Array.isArray(obj[k])) return obj[k] as T[];
    }
  }
  return [];
}

/** Extract pagination metadata from a wrapper response. */
export interface PaginationInfo {
  total: number;
  page: number;
  per_page: number;
  pages: number;
  has_next?: boolean;
  has_prev?: boolean;
}

export function extractPagination(data: unknown): PaginationInfo | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const obj = data as Record<string, unknown>;
  // Some endpoints nest pagination, others inline it
  const pag = (obj.pagination ?? obj) as Record<string, unknown>;
  if (typeof pag.total === 'number') {
    return {
      total: pag.total as number,
      page: (pag.page as number) ?? 1,
      per_page: (pag.per_page as number) ?? 25,
      pages: (pag.pages as number) ?? 1,
      has_next: pag.has_next as boolean | undefined,
      has_prev: pag.has_prev as boolean | undefined,
    };
  }
  return null;
}
